import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { SlackIntegration } from "../types";
import { 
  fetchSlackIntegration, 
  upsertSlackIntegration, 
  deleteSlackIntegration,
  updateSlackSettings
} from "../database";

/**
 * Hook for managing Slack integration data storage
 */
export const useSlackStorage = (aiStaffId: string) => {
  const { toast } = useToast();
  
  const loadIntegration = async (): Promise<SlackIntegration | null> => {
    try {
      logger.info({
        message: "Loading Slack integration",
        context: { aiStaffId }
      });
      
      const data = await fetchSlackIntegration(aiStaffId);
      return data;
    } catch (error) {
      logger.error({
        message: "Error loading Slack integration",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      
      return null;
    }
  };
  
  const saveIntegration = async (
    webhookUrl: string,
    slackToken: string,
    triggerTag: string = "@marketing",
    isFileProcessingEnabled: boolean = true,
    isAutoResponsesEnabled: boolean = true
  ): Promise<boolean> => {
    try {
      logger.info({
        message: "Saving Slack integration",
        context: { aiStaffId, triggerTag }
      });
      
      await upsertSlackIntegration(
        aiStaffId,
        webhookUrl,
        slackToken,
        triggerTag,
        isFileProcessingEnabled,
        isAutoResponsesEnabled
      );
      
      logger.info({
        message: "Successfully saved Slack integration",
        context: { aiStaffId }
      });
      
      return true;
    } catch (error) {
      logger.error({
        message: "Error saving Slack integration",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      
      toast({
        title: "Failed to save integration",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
      
      throw error;
    }
  };
  
  const deleteIntegration = async (): Promise<boolean> => {
    try {
      await deleteSlackIntegration(aiStaffId);
      return true;
    } catch (error) {
      logger.error({
        message: "Error deleting Slack integration",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      
      return false;
    }
  };
  
  const saveSettings = async (settings: Partial<SlackIntegration>): Promise<void> => {
    try {
      await updateSlackSettings(aiStaffId, settings);
    } catch (error) {
      logger.error({
        message: "Error saving Slack settings",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId, settings }
      });
      throw error;
    }
  };
  
  return {
    loadIntegration,
    saveIntegration,
    deleteIntegration,
    saveSettings
  };
};
